import { validatePermission } from 'actions';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Text } from 'v3/components';
import { useTravel } from 'v3/pages/Travel';
import { useSnackbar } from 'v3/components/Snackbar';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import { useModal } from 'hooks';
import FreightValuesTravelDetails from 'v3/components/FreightValues/FreightValuesTravelDetails';
import Tab from 'v3/components/FreightValues/components/Tabs';
import { Col } from 'react-bootstrap';
import { toReplacementPayload } from 'v3/components/FreightValues/utils';
import { MarginLiberationModal } from './MarginLiberation';
import { MinimumMarginModal } from '..';

export default function LoadFreightValues({
  valuesRef,
  freightValuesRef,
  updateFreightValues,
  freightValuesReplaceRef,
}) {
  const { isTravelBlocked, isTravelReproved, margins } = useContext(BlockedTravelContext);
  const snackbar = useSnackbar();
  const [hasPermissionToEdit, setPermissionToEdit] = useState();
  const lowerMarginModal = useModal();
  const [tabSelected, setTabSelected] = useState('tab1');

  const travel = useTravel();

  const editableTravelStatus = useMemo(() => {
    const editableStatus = ['ST2', 'ST8', 'ENDED_LOADING', 'RELEASED_COLLECTION', 'BLOCKED'];
    const editable = travel?.statuses
      ?.filter((item) => editableStatus.includes(item?.code))
      ?.map((status) => status.id);
    return editable;
  }, [travel?.statuses]);

  const isReplacement = useMemo(() => {
    return travel?.data?.loads?.isReplacement;
  }, [travel?.data?.loads?.isReplacement]);

  useEffect(() => {
    setPermissionToEdit(validatePermission('ALTERAR_VIAGEM'));
  }, []);

  async function handleSubmit(submitOptions) {
    try {
      const freightValues = await freightValuesRef?.current?.getValues({
        validateAttended: true,
      });

      const freightValuesReplacement = await freightValuesReplaceRef?.current?.getValues({
        validateAttended: true,
      });
      if (!freightValues) {
        snackbar.show(<Text>Verifique os campos obrigatórios.</Text>, {
          type: 'error',
        });
        return false;
      }

      if (isReplacement && !freightValuesReplacement) {
        snackbar.show(<Text>Verifique os campos obrigatórios da troca de nota!</Text>, {
          type: 'error',
        });
        return false;
      }

      const replacementPayload =
        isReplacement && freightValuesReplacement && toReplacementPayload(freightValuesReplacement);

      if (!submitOptions?.confirmMinimiumMargin) {
        const currentReplacement = travel?.data?.loads?.replacements?.[0];
        const isMarginAbove = await travel.isMarginAbove({
          actualMargin: freightValues?.travelMargin,
          isMarginChanged: travel?.data?.margin !== freightValues?.travelMargin,
          validateReplacement:
            replacementPayload &&
            isReplacement &&
            currentReplacement.margin !== replacementPayload.margin,
          replacement: replacementPayload,
        });

        if (isMarginAbove) {
          lowerMarginModal.open();
          return false;
        }
      }

      await updateFreightValues(freightValues, travel?.data, replacementPayload);
      await travel.fetchTravel();
      return true;
    } catch (error) {
      return false;
      //
    }
  }

  const [openLiberationModal, setOpenLiberationModal] = useState(false);

  const shouldBeAbleToEditValues = useMemo(() => {
    let travelStatus = false;
    let withoutPending = false;
    if (editableTravelStatus?.includes(travel?.data?.statusId)) {
      travelStatus = true;
    }
    if (
      (travel?.data?.travelMarginLiberation?.length > 0 &&
        travel?.data?.travelMarginLiberation?.every((item) => item?.userApprovalId !== null)) ||
      travel?.data?.travelMarginLiberation?.length === 0
    ) {
      withoutPending = true;
    }
    return travelStatus && withoutPending;
  }, [travel?.data, editableTravelStatus]);

  async function handleConfirmLowerMargin(value) {
    const submitted = await handleSubmit(value);
    if (submitted) {
      freightValuesRef?.current?.finishEditing?.();
      freightValuesReplaceRef?.current?.finishEditing?.();
    }
    return submitted;
  }

  const hasFreightValuesError = useMemo(() => {
    return Object.keys(freightValuesRef?.current?.errors || {}).length > 0;
  }, [freightValuesRef?.current?.errors]);

  const hasFreightValuesReplaceError = useMemo(() => {
    return Object.keys(freightValuesReplaceRef?.current?.errors || {}).length > 0;
  }, [freightValuesReplaceRef?.current?.errors]);

  const lastDestination = useMemo(() => {
    if (!travel?.data?.loads?.loadDestinations?.length) {
      return null;
    }
    return travel?.data?.loads?.loadDestinations?.[
      travel?.data?.loads?.loadDestinations?.length - 1
    ];
  }, [travel?.data?.loads?.loadDestinations]);

  const isNational = useMemo(() => {
    if (!travel?.data?.loads?.loadOrigins?.length) {
      return null;
    }

    if (!lastDestination) {
      return null;
    }

    const origin = travel?.data?.loads?.loadOrigins?.[0];

    return origin?.countrySlug === 'br' && lastDestination?.countrySlug === 'br';
  }, [travel?.data?.loads?.loadOrigins, lastDestination]);

  return (
    <>
      <MarginLiberationModal
        isOpen={openLiberationModal}
        onClose={() => setOpenLiberationModal(false)}
      />
      <MinimumMarginModal
        isOpen={lowerMarginModal.isOpen}
        onClose={lowerMarginModal.close}
        confirm={handleConfirmLowerMargin}
      />
      <div ref={valuesRef}>
        {isReplacement && (
          <Col className="d-flex">
            <Tab
              active={tabSelected === 'tab1'}
              onClick={() => setTabSelected('tab1')}
              error={hasFreightValuesError}
            >
              1ª Troca Nota
            </Tab>
            <Tab
              active={tabSelected === 'tab2'}
              onClick={() => setTabSelected('tab2')}
              error={hasFreightValuesReplaceError}
            >
              2ª Troca Nota
            </Tab>
          </Col>
        )}
        <div
          className={tabSelected === 'tab1' ? 'd-block' : 'd-none'}
          style={{
            width: '100%',
          }}
        >
          <FreightValuesTravelDetails
            ref={freightValuesRef}
            disableEditing={
              !travel.data?.driver ||
              !travel.data?.vehicle?.[0] ||
              (isTravelBlocked && !isTravelReproved) ||
              (travel?.data?.policy?.id && travel.isDisabledGROAnalysis && !isTravelReproved)
            }
            showEditing={(isTravelReproved || shouldBeAbleToEditValues) && hasPermissionToEdit}
            onLiberation={() => setOpenLiberationModal(true)}
            showLiberationButton={travel?.data?.travelStatus?.code !== 'ST7'}
            shippingCompany={travel?.data?.shipper}
            handleSubmit={handleSubmit}
            isNational={isNational}
          />
        </div>
        <div
          className={tabSelected === 'tab2' && isReplacement ? 'd-block' : 'd-none'}
          style={{
            width: '100%',
          }}
        >
          <FreightValuesTravelDetails
            ref={freightValuesReplaceRef}
            disableEditing={
              !travel.data?.driver ||
              !travel.data?.vehicle?.[0] ||
              (isTravelBlocked && !isTravelReproved) ||
              (travel?.data?.policy?.id && travel.isDisabledGROAnalysis && !isTravelReproved)
            }
            showEditing={(isTravelReproved || shouldBeAbleToEditValues) && hasPermissionToEdit}
            onLiberation={() => setOpenLiberationModal(true)}
            showLiberationButton={travel?.data?.travelStatus?.code !== 'ST7'}
            shippingCompany={travel?.data?.shipper}
            handleSubmit={handleSubmit}
            isNational={isNational}
          />
        </div>
      </div>
    </>
  );
}
